import React, { useState, memo } from 'react'
import cn, { Argument as ClassName } from 'classnames'

import { WidgetLayout } from '../../../lib/widget-layout'
import Text from '../../../ui/Text'
import { Button } from '../../../lib/button'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { NfcTag } from '../../../common/redux/api/web-api'
import { ChargersPagination } from '../../../lib/chargers-pagination'
import DropdownMenu, { DropdownMenuItem } from '../../../common/components/DropdownMenu'
import useText from '../../../common/hooks/useText'
import { EditIcon } from '../../../common/icons/edit-icon/edit-icon'
import TrashIcon from '../../../lib/icons/trash-icon/trash-icon'

export type CompanyRfidWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    companyId?: string
    nfcTags?: NfcTag[]
    openNfcTag: (nfcTag: NfcTag) => void
    removeTag: (nfcTag: NfcTag) => void
}

export const CompanyRfidWidget = memo(function CompanyRfidWidget({
    className,
    style,
    nfcTags,
    openNfcTag,
    removeTag,
    companyId,
}: CompanyRfidWidgetProps): React.ReactElement {
    const text = useText()
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize] = useState(20)
    const offset = currentPage * pageSize

    const handlePageClick = (selected: number) => {
        setCurrentPage(selected)
    }
    return (
        <WidgetLayout
            data-testid="table-rfid"
            className={cn(className)}
            style={style}
            title={text('lblRFID')}
            headerButtons={[
                <Button
                    dataTestId="table-rfid-add-btn"
                    type="button"
                    onClick={() => openNfcTag({ companyId })}
                    code="btnAdd"
                    small
                />,
            ]}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell data-testid="table-idrfid-heading">
                            <Text code="lblId" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-comment-heading">
                            <Text code="lblComment" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-editrfid-heading"></TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {nfcTags?.slice(offset, offset + pageSize).map((tag) => (
                        <TableRow key={tag?.id}>
                            <TableBodyCell data-testid="table-idrfid-body">{tag?.id}</TableBodyCell>
                            <TableBodyCell data-testid="table-comment-body">{tag?.comment}</TableBodyCell>
                            <TableBodyCell>
                                <DropdownMenu className="ml-auto" data-testid="menu-toggle-rfid">
                                    <DropdownMenuItem
                                        onClick={() => openNfcTag(tag)}
                                        text={text('btnEdit')}
                                        icon={<EditIcon size={14} />}
                                        key="btnEditRfid"
                                        data-testid="table-rfid-body-edit"
                                    />
                                    <DropdownMenuItem
                                        onClick={() => removeTag(tag)}
                                        text={text('btnRemove')}
                                        icon={<TrashIcon size={14} />}
                                        key="btnRemoveRfid"
                                        data-testid="table-rfid-body-remove"
                                    />
                                </DropdownMenu>
                            </TableBodyCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {nfcTags && nfcTags?.length > 20 && (
                <ChargersPagination
                    setPageIndex={handlePageClick}
                    totalCount={nfcTags?.length}
                    pageSize={pageSize}
                    pageIndex={currentPage}
                    displayedChargers={pageSize}
                />
            )}
            {!nfcTags && <div className="shimmer-container shimmer-xsmall" data-testid="rfid-loading"></div>}
        </WidgetLayout>
    )
})
